import type { RelatedLink } from "@/components/public/related-pages"

const L = {
  // Audiences
  forIndependent: {
    href: "/for/independent-agents",
    title: "For independent agents",
    description: "Stand out from corporate chains with branded property video.",
  },
  forLetting: {
    href: "/for/letting-agents",
    title: "For letting agents",
    description: "Fill vacancies faster with scroll-stopping rental video ads.",
  },
  forMultiBranch: {
    href: "/for/multi-branch",
    title: "For multi-branch agencies",
    description: "Consistent, branded video across every branch and office.",
  },
  forHolidayLets: {
    href: "/for/holiday-lets",
    title: "For holiday lets",
    description: "Cinematic video tours that drive direct bookings.",
  },
  forBtl: {
    href: "/for/btl-landlords",
    title: "For BTL landlords",
    description: "Reduce voids and reach younger tenants with social video.",
  },
  forCommercial: {
    href: "/for/commercial-agents",
    title: "For commercial agents",
    description: "Showcase commercial space with cinematic, data-driven video.",
  },
  forNewHomes: {
    href: "/for/new-homes-developers",
    title: "For new homes developers",
    description: "Phase-specific launch ads from CGI, lifestyle, and housetypes.",
  },
  forScottish: {
    href: "/for/scottish-agents",
    title: "For Scottish agents",
    description: "Built for Home Reports, missives, and the Scottish market.",
  },
  // Versus competitors
  vsCapcut: {
    href: "/vs/capcut",
    title: "QuiteRight vs CapCut",
    description: "Why purpose-built property video beats DIY editing apps.",
  },
  vsBoxbrownie: {
    href: "/vs/boxbrownie",
    title: "QuiteRight vs BoxBrownie",
    description: "Photo retouching is great. Video is what stops the scroll.",
  },
  vsGiraffe360: {
    href: "/vs/giraffe360",
    title: "QuiteRight vs Giraffe360",
    description: "Where 3D tours win, where social-first video wins.",
  },
  vsMatterport: {
    href: "/vs/matterport",
    title: "QuiteRight vs Matterport",
    description: "Virtual tours vs the videos buyers actually share.",
  },
  vsVideographer: {
    href: "/vs/videographer",
    title: "QuiteRight vs videographer",
    description: "60 seconds vs five days, every listing vs your top 10.",
  },
  // Quiteright-vs (top-level)
  qrVsCanva: {
    href: "/quiteright-vs-canva",
    title: "QuiteRight vs Canva",
    description: "Purpose-built property video vs generic design tools.",
  },
  qrVsDiy: {
    href: "/quiteright-vs-diy-video",
    title: "QuiteRight vs DIY video",
    description: "Professional results without the editing learning curve.",
  },
  qrVsAgency: {
    href: "/quiteright-vs-agency",
    title: "QuiteRight vs an agency",
    description: "Agency-quality video at a fraction of the cost.",
  },
  // Use cases
  ucJustListed: {
    href: "/use-cases/just-listed-video",
    title: "Just-listed video",
    description: "New instruction, straight to feed within minutes.",
  },
  ucPriceReduction: {
    href: "/use-cases/price-reduction-video",
    title: "Price-reduction video",
    description: "Reframe a price drop as a buying opportunity.",
  },
  ucOpenHouse: {
    href: "/use-cases/open-house-video",
    title: "Open-house video",
    description: "Fill every slot before Saturday arrives.",
  },
  ucWalkthrough: {
    href: "/use-cases/property-walkthrough-video",
    title: "Walkthrough video",
    description: "Phone-native walkthrough for every listing.",
  },
  ucNewBuild: {
    href: "/use-cases/new-build-launch-video",
    title: "New-build launch video",
    description: "Phase-specific ads for every release.",
  },
  // Hubs
  hubUseCases: {
    href: "/use-cases",
    title: "All use cases",
    description: "One pipeline, every listing moment.",
  },
  hubGuides: {
    href: "/guides",
    title: "Guides",
    description: "Free guides for UK estate agents and home sellers.",
  },
  hubTools: {
    href: "/tools",
    title: "Tools & calculators",
    description: "Free property calculators for agents and buyers.",
  },
  hubIntegrations: {
    href: "/integrations",
    title: "Portal integrations",
    description: "Rightmove, Zoopla, and OnTheMarket — paste a URL, get a video.",
  },
  hubBest: {
    href: "/best",
    title: "Best estate agents 2026",
    description: "Top-ranked agents across the UK.",
  },
  hubAreas: {
    href: "/areas",
    title: "Area guides",
    description: "Neighbourhood guides for buyers and sellers.",
  },
  hubInsights: {
    href: "/insights",
    title: "Local market insights",
    description: "Live property prices, transactions, and trends by city.",
  },
  hubGlossary: {
    href: "/glossary",
    title: "Property glossary",
    description: "Property marketing terms explained.",
  },
  hubDirectory: {
    href: "/directory",
    title: "Agent directory",
    description: "Browse and compare local estate agents.",
  },
  // Integrations
  intRightmove: {
    href: "/integrations/rightmove",
    title: "Rightmove video ads",
    description: "Paste a Rightmove URL, get a video in 60 seconds.",
  },
  intZoopla: {
    href: "/integrations/zoopla",
    title: "Zoopla video ads",
    description: "Branded video ads from any Zoopla listing.",
  },
  intOnTheMarket: {
    href: "/integrations/onthemarket",
    title: "OnTheMarket video ads",
    description: "Video ads from any OnTheMarket URL.",
  },
  // Selected guides
  guideRightmoveOpt: {
    href: "/guides/rightmove-listing-optimisation",
    title: "Rightmove listing optimisation",
    description: "Photography, descriptions, and video — a complete checklist.",
  },
  guidePortalsCompare: {
    href: "/guides/zoopla-vs-rightmove-vs-onthemarket",
    title: "Zoopla vs Rightmove vs OnTheMarket",
    description: "Traffic, fees, features, and which portal strategy fits.",
  },
  guideInstagramReels: {
    href: "/guides/instagram-reels-estate-agents",
    title: "Instagram Reels for estate agents",
    description: "How to win instructions and attract buyers on Reels.",
  },
  guideTiktok: {
    href: "/guides/tiktok-estate-agents",
    title: "TikTok for estate agents",
    description: "Property marketing without trending dances.",
  },
  guideSocial: {
    href: "/guides/social-media-estate-agents",
    title: "Social media for estate agents",
    description: "Platform-by-platform strategies, ROI, and content ideas.",
  },
  guideVideoMarketing: {
    href: "/guides/property-video-marketing",
    title: "Property video marketing",
    description: "Why video matters and the five types every agent needs.",
  },
  guideScript: {
    href: "/guides/property-video-script-writing",
    title: "Property video script writing",
    description: "How to write scripts that stop the scroll.",
  },
  guideRental: {
    href: "/guides/marketing-rental-property",
    title: "Marketing rental properties",
    description: "Practical guide for letting agents and landlords.",
  },
  guideMarketing: {
    href: "/guides/property-marketing-checklist",
    title: "Property marketing checklist",
    description: "An interactive 24-point checklist for every listing.",
  },
  guideWinning: {
    href: "/guides/winning-instructions",
    title: "Winning instructions",
    description: "A six-step playbook for winning more instructions.",
  },
  guideAppraisal: {
    href: "/guides/market-appraisal-presentation",
    title: "Market appraisal presentation",
    description: "Deliver appraisals that win instructions.",
  },
  guideFees: {
    href: "/guides/estate-agent-fees",
    title: "Estate agent fees",
    description: "How much do estate agents charge in 2026?",
  },
  guideChooseAgent: {
    href: "/guides/how-to-choose-estate-agent",
    title: "How to choose an estate agent",
    description: "An 8-point checklist for sellers.",
  },
  guideRoi: {
    href: "/guides/cost-per-lead-video-ads-vs-traditional-property-marketing",
    title: "Cost per lead: video ads vs traditional",
    description: "How video ads compare to portals, print, and leaflets.",
  },
  guidePaidVsOrganic: {
    href: "/guides/paid-video-ads-vs-organic-social-estate-agents",
    title: "Paid video ads vs organic social",
    description: "Reach, cost, ROI, and the right split for your agency.",
  },
  guideRetargeting: {
    href: "/guides/retargeting-vs-cold-audience-property-video-ads",
    title: "Retargeting vs cold audience",
    description: "CTR benchmarks and the optimal budget split.",
  },
  // Static pages
  pageWhy: {
    href: "/why-quiteright",
    title: "Why QuiteRight",
    description: "The ROI of property video marketing.",
  },
  pageHowItWorks: {
    href: "/how-it-works",
    title: "How it works",
    description: "Paste a listing, pick a template, get a video.",
  },
  pageFeatures: {
    href: "/features",
    title: "Features",
    description: "Templates, voiceover, music, and multi-platform publishing.",
  },
  pageExamples: {
    href: "/examples",
    title: "Examples",
    description: "See property video ads created with QuiteRight.",
  },
  pagePricing: {
    href: "/pricing",
    title: "Pricing",
    description: "Three plans: Agent, Managed, and Fully Managed.",
  },
} satisfies Record<string, RelatedLink>

export const RELATED_PAGES: Record<string, RelatedLink[]> = {
  // /for/* leaves
  "for/independent-agents": [L.forLetting, L.forMultiBranch, L.guideWinning, L.guideAppraisal, L.hubGuides, L.hubUseCases],
  "for/letting-agents": [L.forBtl, L.forHolidayLets, L.guideRental, L.ucJustListed, L.guideInstagramReels, L.hubGuides],
  "for/multi-branch": [L.forIndependent, L.forNewHomes, L.guideMarketing, L.guideRoi, L.hubUseCases, L.hubTools],
  "for/holiday-lets": [L.forBtl, L.forLetting, L.guideInstagramReels, L.guideTiktok, L.hubUseCases, L.guideRental],
  "for/btl-landlords": [L.forLetting, L.forHolidayLets, L.guideRental, L.ucWalkthrough, L.guideInstagramReels, L.hubTools],
  "for/commercial-agents": [L.forIndependent, L.forNewHomes, L.forMultiBranch, L.guideVideoMarketing, L.hubUseCases, L.hubGuides],
  "for/new-homes-developers": [L.forMultiBranch, L.forCommercial, L.ucNewBuild, L.guidePaidVsOrganic, L.guideRetargeting, L.hubUseCases],
  "for/scottish-agents": [L.forIndependent, L.forMultiBranch, L.guideAppraisal, L.guideMarketing, L.hubGuides, L.hubUseCases],

  // /vs/* leaves
  "vs/capcut": [L.vsBoxbrownie, L.vsGiraffe360, L.vsMatterport, L.vsVideographer, L.qrVsDiy, L.guideScript],
  "vs/boxbrownie": [L.vsCapcut, L.vsGiraffe360, L.vsVideographer, L.qrVsCanva, L.guideRightmoveOpt, L.hubIntegrations],
  "vs/giraffe360": [L.vsMatterport, L.vsBoxbrownie, L.vsCapcut, L.qrVsAgency, L.guideVideoMarketing, L.hubUseCases],
  "vs/matterport": [L.vsGiraffe360, L.vsBoxbrownie, L.vsVideographer, L.qrVsAgency, L.guideVideoMarketing, L.hubUseCases],
  "vs/videographer": [L.qrVsAgency, L.vsCapcut, L.vsGiraffe360, L.qrVsDiy, L.guideMarketing, L.hubGuides],

  // /quiteright-vs-* leaves
  "quiteright-vs-canva": [L.qrVsDiy, L.qrVsAgency, L.vsCapcut, L.guideScript, L.guideMarketing, L.hubGuides],
  "quiteright-vs-diy-video": [L.qrVsCanva, L.qrVsAgency, L.vsCapcut, L.vsVideographer, L.guideScript, L.hubUseCases],
  "quiteright-vs-agency": [L.qrVsCanva, L.qrVsDiy, L.vsVideographer, L.guideRoi, L.guidePaidVsOrganic, L.hubGuides],

  // /use-cases/* leaves
  "use-cases/just-listed-video": [L.ucPriceReduction, L.ucOpenHouse, L.ucWalkthrough, L.guideInstagramReels, L.guideRightmoveOpt, L.hubUseCases],
  "use-cases/price-reduction-video": [L.ucJustListed, L.ucWalkthrough, L.guidePortalsCompare, L.guideRetargeting, L.hubGuides, L.hubUseCases],
  "use-cases/open-house-video": [L.ucJustListed, L.ucWalkthrough, L.guideAppraisal, L.guideMarketing, L.hubUseCases, L.hubGuides],
  "use-cases/property-walkthrough-video": [L.ucJustListed, L.ucOpenHouse, L.guideScript, L.guideVideoMarketing, L.guideRightmoveOpt, L.hubGuides],
  "use-cases/new-build-launch-video": [L.forNewHomes, L.ucWalkthrough, L.guidePaidVsOrganic, L.guideRetargeting, L.hubUseCases, L.hubGuides],

  // /integrations/* leaves
  "integrations/rightmove": [L.intZoopla, L.intOnTheMarket, L.guideRightmoveOpt, L.guidePortalsCompare, L.hubIntegrations, L.hubGuides],
  "integrations/zoopla": [L.intRightmove, L.intOnTheMarket, L.guidePortalsCompare, L.hubIntegrations, L.hubGuides, L.hubUseCases],
  "integrations/onthemarket": [L.intRightmove, L.intZoopla, L.guidePortalsCompare, L.hubIntegrations, L.hubGuides, L.hubUseCases],
}

export function relatedFor(key: string): RelatedLink[] {
  return RELATED_PAGES[key] ?? []
}
